import { useState, type ReactElement, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Skeleton, Tooltip } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityActionMenu, type EntityAction } from './EntityActionMenu.js';
import { EmptyState } from './EmptyState.js';

export interface EntityRelatedRow {
  id: string;
  title: string;
  className?: string;
  href?: string;
  onOpen?: () => void;
  status?: ReactNode;
  meta?: ReactNode;
  value?: ReactNode;
  leading?: ReactNode;
  visibleActions?: ReactNode;
  actions?: EntityAction[];
}
export function EntityRelatedList({
  rows,
  filter,
  action,
  emptyText,
  loading,
  error,
  className,
}: {
  rows: EntityRelatedRow[];
  filter?: ReactNode;
  action?: ReactNode;
  emptyText: string;
  loading?: boolean;
  error?: Error | null;
  className?: string;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(20);
  return (
    <div className={className}>
      {filter || (action && rows.length > 0) ? (
        <div className="entity-list-toolbar">
          <div>{filter}</div>
          <div>{rows.length > 0 ? action : null}</div>
        </div>
      ) : null}
      {error ? (
        <Alert
          type="error"
          showIcon
          title={t('crm.entity_detail.load_error')}
          description={error.message}
        />
      ) : loading ? (
        <Skeleton active title={false} paragraph={{ rows: 4 }} />
      ) : rows.length === 0 ? (
        <EmptyState text={emptyText} action={action} />
      ) : (
        <div className="entity-related-list">
          {rows.slice(0, limit).map((row) => (
            <div
              key={row.id}
              className={['entity-related-row', row.className].filter(Boolean).join(' ')}
              onClick={(event) => {
                if (
                  (event.target as HTMLElement).closest(
                    'a,button,input,label,select,.ant-select,.ant-select-dropdown,[role="combobox"],[role="option"],[role="checkbox"],[role="menuitem"],[role="menu"]',
                  )
                )
                  return;
                if (row.href) navigate(row.href);
                else row.onOpen?.();
              }}
            >
              {row.leading ? <div className="entity-row-controls">{row.leading}</div> : null}
              <div className="entity-row-body">
                <div className="entity-row-title">
                  <Tooltip title={row.title}>
                    {row.href ? (
                      <Link to={row.href}>{row.title}</Link>
                    ) : row.onOpen ? (
                      <button type="button" onClick={row.onOpen}>
                        {row.title}
                      </button>
                    ) : (
                      <span>{row.title}</span>
                    )}
                  </Tooltip>
                  {row.status}
                </div>
                {row.meta ? <div className="entity-meta">{row.meta}</div> : null}
              </div>
              {row.value ? <div className="entity-row-value">{row.value}</div> : null}
              {row.visibleActions}
              {row.actions?.length ? (
                <EntityActionMenu
                  actions={row.actions}
                  label={t('crm.entity_detail.row_actions', { title: row.title })}
                />
              ) : null}
            </div>
          ))}
          {rows.length > limit ? (
            <Button type="link" onClick={() => setLimit((value) => value + 20)}>
              {t('crm.entity_detail.show_more')}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
